import React, { useState, useEffect } from 'react'
import { plants } from '../data/plants'
import { useGardenContext } from '../context/GardenContext'
import { 
  getZipCodeData, 
  hasZipCode, 
  addZipCode,
  ZipCodeData 
} from '../data/zipCodeDatabase'
import { getFrostDates, FrostDateResult } from '../data/zipCodeApi'
import ZoneFilter, { plantSurvivesInZone } from '../components/ZoneFilter'

interface PlantingTask {
  id: string
  plantId: string
  plantName: string
  task: 'start-indoors' | 'direct-sow' | 'transplant' | 'harvest'
  date: Date
  completed: boolean
}

const Calendar: React.FC = () => {
  const { 
    gardens, 
    myPlants, 
    addToMyPlants, 
    removeFromMyPlants,
    zipCode,
    setZipCode,
    zone,
    setZone,
    lastFrost: lastFrostString,
    setLastFrost: setLastFrostString,
    firstFrost: firstFrostString,
    setFirstFrost: setFirstFrostString,
    dataSource,
    setDataSource,
    confidence,
    setConfidence
  } = useGardenContext()
  
  // Convert string dates to Date objects for local use
  const parseDate = (dateString: string | null): Date | null => {
    if (!dateString) return null
    const date = new Date(dateString)
    return isNaN(date.getTime()) ? null : date
  }
  
  const [lastFrost, setLastFrost] = useState<Date | null>(() => parseDate(lastFrostString))
  const [firstFrost, setFirstFrost] = useState<Date | null>(() => parseDate(firstFrostString))
  const [isLoading, setIsLoading] = useState(false)
  const [tasks, setTasks] = useState<PlantingTask[]>([])
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth())
  const [selectedYear] = useState(new Date().getFullYear())
  
  // Plant selection state
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('')
  const [selectedPlant, setSelectedPlant] = useState<string>('')
  const [showPlantInfo, setShowPlantInfo] = useState<string | null>(null)
  const [selectedFilterZone, setSelectedFilterZone] = useState<string>('')
  
  // Use myPlants from context - shared with Garden Designer
  const selectedPlants = myPlants

  // Vegetable subcategories
  const vegetableSubcategories: Record<string, string> = {
    'lettuce': 'Lettuce', 'spinach': 'Spinach', 'kale': 'Kale & Collards', 'chard': 'Chard',
    'cabbage': 'Cabbage & Brussels', 'broccoli': 'Broccoli & Cauliflower', 'tomato': 'Tomatoes',
    'pepper': 'Peppers', 'cucumber': 'Cucumbers', 'squash': 'Squash & Pumpkins', 'zucchini': 'Zucchini',
    'bean': 'Beans', 'pea': 'Peas', 'carrot': 'Carrots', 'beet': 'Beets', 'radish': 'Radishes',
    'turnip': 'Turnips & Rutabagas', 'potato': 'Potatoes', 'sweetpotato': 'Sweet Potatoes',
    'onion': 'Onions & Garlic', 'corn': 'Corn', 'eggplant': 'Eggplants', 'okra': 'Okra',
    'melon': 'Melons', 'pumpkin': 'Pumpkins', 'asparagus': 'Asparagus', 'artichoke': 'Artichokes',
    'celery': 'Celery', 'leek': 'Leeks', 'shallot': 'Shallots', 'bok choy': 'Asian Greens',
    'arugula': 'Arugula', 'mustard': 'Mustard Greens', 'collard': 'Collards', 'cress': 'Cress',
    'endive': 'Endive & Chicory', 'escarole': 'Escarole', 'mizuna': 'Asian Greens',
    'tatsoi': 'Asian Greens', 'pac choi': 'Asian Greens', 'radicchio': 'Chicory',
    'sorrel': 'Sorrel', 'watercress': 'Watercress', 'parsnip': 'Parsnips',
    'salsify': 'Root Vegetables', 'horseradish': 'Root Vegetables', 'ginger': 'Root Vegetables',
    'turmeric': 'Root Vegetables',
  }

  const getPlantSubcategory = (plant: typeof plants[0]): string => {
    const nameLower = plant.name.toLowerCase()
    for (const [key, value] of Object.entries(vegetableSubcategories)) {
      if (nameLower.includes(key)) return value
    }
    return 'Other Vegetables'
  }

  const getSubcategories = (category: string): string[] => {
    if (category !== 'vegetable') return []
    const subs = new Set<string>()
    plants.filter(p => p.category === 'vegetable').forEach(p => {
      subs.add(getPlantSubcategory(p))
    })
    return Array.from(subs).sort()
  }

  // Filter plants by category, subcategory, and zone
  const filteredPlants = selectedCategory 
    ? plants.filter(p => {
        // Check category
        let categoryMatch = false
        if (selectedCategory === 'tree') {
          categoryMatch = p.category === 'tree' || p.category === 'nut'
        } else if (selectedCategory === 'vegetable' && selectedSubcategory) {
          categoryMatch = p.category === 'vegetable' && getPlantSubcategory(p) === selectedSubcategory
        } else {
          categoryMatch = p.category === selectedCategory
        }
        
        // Check zone
        const zoneMatch = p.hardinessZones ? plantSurvivesInZone(p.hardinessZones, selectedFilterZone) : true
        
        return categoryMatch && zoneMatch
      })
    : []

  // Calculate frost dates based on zip code
  const calculateFrostDates = async () => {
    if (!zipCode || zipCode.length !== 5) return
    
    setIsLoading(true)
    
    try {
      let result: FrostDateResult | null = null
      
      // Step 1: Check local database first (fastest)
      if (hasZipCode(zipCode)) {
        const cached = getZipCodeData(zipCode)
        if (cached) {
          result = {
            zipCode: cached.zipCode,
            zone: cached.zone,
            lastFrost: cached.lastFrost,
            firstFrost: cached.firstFrost,
            source: cached.source === 'database' ? 'usda' : 'estimate',
            confidence: 'high'
          }
        }
      }
      
      // Step 2: Use API if not in database
      if (!result) {
        result = await getFrostDates(zipCode)
      }
      
      if (!result) {
        alert('Unable to determine frost dates for this zip code. Please try another.')
        setIsLoading(false)
        return
      }
      
      // Parse frost dates (MM-DD format)
      const [lastFrostMonth, lastFrostDay] = result.lastFrost.split('-').map(Number)
      const lastFrostDate = new Date(selectedYear, lastFrostMonth - 1, lastFrostDay)
      
      setZone(result.zone)
      setLastFrost(lastFrostDate)
      setLastFrostString(lastFrostDate.toISOString())
      if (result.firstFrost) {
        const [firstFrostMonth, firstFrostDay] = result.firstFrost.split('-').map(Number)
        const firstFrostDate = new Date(selectedYear, firstFrostMonth - 1, firstFrostDay)
        setFirstFrost(firstFrostDate)
        setFirstFrostString(firstFrostDate.toISOString())
      }
      setDataSource(result.source)
      setConfidence(result.confidence)
      
      // Regenerate tasks if plants are selected
      if (myPlants.length > 0) {
        generateTasks(lastFrostDate, myPlants)
      }
    } catch (error) {
      console.error('Error fetching frost dates:', error)
      alert('Error looking up zip code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Sync local Date state with context string state on mount and when context changes
  useEffect(() => {
    setLastFrost(parseDate(lastFrostString))
  }, [lastFrostString])

  useEffect(() => {
    setFirstFrost(parseDate(firstFrostString))
  }, [firstFrostString])

  // Generate tasks when myPlants or frost date changes
  useEffect(() => {
    if (lastFrost && myPlants.length > 0) {
      generateTasks(lastFrost, myPlants)
    }
  }, [lastFrost, myPlants])

  const generateTasks = (frostDate: Date, plantIds: string[]) => {
    const year = frostDate.getFullYear()
    const newTasks: PlantingTask[] = []

    plantIds.forEach(plantId => {
      const plant = plants.find(p => p.id === plantId)
      if (!plant) return

      // Start indoors (6-8 weeks before last frost for tomatoes, peppers, etc.)
      const needsIndoorStart = ['tomato', 'pepper', 'eggplant', 'broccoli', 'cauliflower', 'cabbage', 'kale', 'collard']
        .some(keyword => plant.name.toLowerCase().includes(keyword))
      
      if (needsIndoorStart) {
        const startIndoors = new Date(frostDate)
        startIndoors.setDate(startIndoors.getDate() - 42) // 6 weeks before
        newTasks.push({
          id: `${plant.id}-start-${year}`,
          plantId: plant.id,
          plantName: plant.name,
          task: 'start-indoors',
          date: startIndoors,
          completed: false
        })
      }

      // Direct sow (on or after last frost, adjusted by plant)
      const directSow = new Date(frostDate)
      // Cold-hardy plants can go out 2-4 weeks before last frost
      const isColdHardy = ['lettuce', 'spinach', 'kale', 'pea', 'radish', 'carrot', 'beet', 'onion', 'garlic']
        .some(keyword => plant.name.toLowerCase().includes(keyword))
      if (isColdHardy) {
        directSow.setDate(directSow.getDate() - 14) // 2 weeks before
      }
      newTasks.push({
        id: `${plant.id}-sow-${year}`,
        plantId: plant.id,
        plantName: plant.name,
        task: 'direct-sow',
        date: directSow,
        completed: false
      })

      // Harvest (after days to harvest from sow date)
      const harvest = new Date(directSow)
      harvest.setDate(harvest.getDate() + plant.daysToHarvest)
      newTasks.push({
        id: `${plant.id}-harvest-${year}`,
        plantId: plant.id,
        plantName: plant.name,
        task: 'harvest',
        date: harvest,
        completed: false
      })
    })

    setTasks(newTasks.sort((a, b) => a.date.getTime() - b.date.getTime()))
  }

  const addPlantToCalendar = () => {
    if (!selectedPlant || myPlants.includes(selectedPlant)) return
    
    addToMyPlants(selectedPlant)
    setSelectedPlant('')
    
    if (lastFrost) {
      generateTasks(lastFrost, [...myPlants, selectedPlant])
    }
  }

  const removePlantFromCalendar = (plantId: string) => {
    removeFromMyPlants(plantId)
    
    if (lastFrost) {
      generateTasks(lastFrost, myPlants.filter(id => id !== plantId))
    }
  }

  const toggleTask = (taskId: string) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ))
  }

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

  const tasksByMonth = tasks.filter(task => task.date.getMonth() === selectedMonth)

  const getTaskLabel = (task: PlantingTask['task']) => {
    switch (task) {
      case 'start-indoors': return '🌱 Start Indoors'
      case 'direct-sow': return '🌿 Direct Sow'
      case 'transplant': return '🪴 Transplant'
      case 'harvest': return '🥕 Harvest'
    }
  }

  const getTaskColor = (task: PlantingTask['task']) => {
    switch (task) {
      case 'start-indoors': return '#4a90d9'
      case 'direct-sow': return '#5cb85c'
      case 'transplant': return '#f0ad4e'
      case 'harvest': return '#d9534f'
    }
  }

  // Get selected plant details
  const selectedPlantData = selectedPlant ? plants.find(p => p.id === selectedPlant) : null
  const plantInfoData = showPlantInfo ? plants.find(p => p.id === showPlantInfo) : null

  // Helper to get soil pH description
  const getPHDescription = (min: number, max: number) => {
    const avg = (min + max) / 2
    if (avg < 6.0) return 'Slightly Acidic'
    if (avg > 7.0) return 'Slightly Alkaline'
    return 'Neutral'
  }

  // Helper to get difficulty level
  const getDifficulty = (plant: typeof plants[0]) => {
    if (plant.daysToHarvest < 40 && plant.daysToGermination < 10) return 'Easy'
    if (plant.daysToHarvest > 80 || plant.daysToGermination > 14) return 'Advanced'
    return 'Moderate'
  }

  // Helper to get beginner tips
  const getBeginnerTips = (plant: typeof plants[0]): string[] => {
    const tips: string[] = []
    
    if (plant.daysToGermination < 7) {
      tips.push('🌱 Quick germinator - great for beginners!')
    }
    if (plant.category === 'herb') {
      tips.push('🌿 Herbs are forgiving and great for learning')
    }
    if (plant.sun === 'full') {
      tips.push('☀️ Needs 6+ hours of direct sunlight daily')
    } else if (plant.sun === 'partial') {
      tips.push('🌤️ Prefers 3-6 hours of sunlight')
    }
    if (plant.water === 'low') {
      tips.push('💧 Drought tolerant - don\'t overwater!')
    } else if (plant.water === 'high') {
      tips.push('💧 Keep soil consistently moist')
    }
    if (plant.companions.length > 0) {
      tips.push(`🤝 Good companions: ${plant.companions.slice(0, 3).join(', ')}`)
    }
    
    return tips
  }

  return (
    <div style={{ padding: '1rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div className="card">
        <h2>📅 Planting Calendar</h2>
        <p>Plan your garden season with personalized planting dates and growing guides.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
        {/* Left Column - Location */}
        <div>
          {/* Location Setup */}
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3>📍 Your Location</h3>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: '0.25rem' }}>Zip Code</label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="12345"
                  maxLength={5}
                  style={{ width: '100%', padding: '0.5rem' }}
                />
              </div>
              <button 
                className="btn btn-primary" 
                onClick={calculateFrostDates}
                disabled={zipCode.length !== 5 || isLoading}
                style={{ alignSelf: 'flex-end' }}
              >
                {isLoading ? '...' : 'Set'}
              </button>
            </div>
            
            {isLoading ? (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fff3cd', borderRadius: '8px', fontSize: '0.9rem' }}>
                <span>⏳ Looking up frost dates...</span>
              </div>
            ) : zone && lastFrost && lastFrost instanceof Date && !isNaN(lastFrost.getTime()) && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#e8f5e9', borderRadius: '8px', fontSize: '0.9rem' }}>
                <div><strong>Zone {zone}</strong> • Last Frost: <strong>{lastFrost.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</strong></div>
                <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                  {dataSource === 'usda' && <span>✓ USDA Official Data</span>}
                  {dataSource === 'open-meteo' && <span>📊 Historical Climate Data (10 years)</span>}
                  {dataSource === 'estimate' && <span>📍 Regional Estimate</span>}
                  {confidence === 'high' && <span> • High Confidence</span>}
                  {confidence === 'medium' && <span> • Medium Confidence</span>}
                  {confidence === 'low' && <span> • Low Confidence</span>}
                </div>
              </div>
            )}
          </div>

          {/* Plant Selection */}
          <div className="card">
            <h3>🌱 Add Plants</h3>
            
            {/* Zone Filter */}
            <div style={{ marginTop: '0.75rem' }}>
              <ZoneFilter 
                selectedZone={selectedFilterZone}
                onZoneChange={(zone) => {
                  setSelectedFilterZone(zone)
                  setSelectedPlant('')
                }}
              />
            </div>
            
            {/* Category Dropdown */}
            <div style={{ marginTop: '0.75rem' }}>
              <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: '0.25rem' }}>
                Step 1: Choose Category
              </label>
              <select 
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value)
                  setSelectedSubcategory('')
                  setSelectedPlant('')
                }}
                style={{ width: '100%', padding: '0.5rem' }}
              >
                <option value="">-- Select Category --</option>
                <option value="vegetable">🥬 Vegetables ({plants.filter(p => p.category === 'vegetable' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedFilterZone))).length})</option>
                <option value="herb">🌿 Herbs ({plants.filter(p => p.category === 'herb' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedFilterZone))).length})</option>
                <option value="flower">🌸 Flowers ({plants.filter(p => p.category === 'flower' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedFilterZone))).length})</option>
                <option value="fruit">🍎 Fruits ({plants.filter(p => p.category === 'fruit' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedFilterZone))).length})</option>
                <option value="berry">🫐 Berries ({plants.filter(p => p.category === 'berry' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedFilterZone))).length})</option>
                <option value="mushroom">🍄 Mushrooms ({plants.filter(p => p.category === 'mushroom' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedFilterZone))).length})</option>
                <option value="tree">🌳 Trees ({plants.filter(p => (p.category === 'tree' || p.category === 'nut') && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedFilterZone))).length})</option>
                <option value="cover_crop">🌾 Cover Crops ({plants.filter(p => p.category === 'cover_crop' && (!p.hardinessZones || plantSurvivesInZone(p.hardinessZones, selectedFilterZone))).length})</option>
              </select>
            </div>

            {/* Subcategory Dropdown (for vegetables) */}
            {selectedCategory === 'vegetable' && (
              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: '0.25rem' }}>
                  Step 2: Choose Type
                </label>
                <select 
                  value={selectedSubcategory}
                  onChange={(e) => {
                    setSelectedSubcategory(e.target.value)
                    setSelectedPlant('')
                  }}
                  style={{ width: '100%', padding: '0.5rem' }}
                >
                  <option value="">-- All Vegetables --</option>
                  {getSubcategories('vegetable').map(sub => (
                    <option key={sub} value={sub}>
                      {sub} ({plants.filter(p => p.category === 'vegetable' && getPlantSubcategory(p) === sub).length})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Plant Dropdown */}
            {selectedCategory && (
              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: '0.25rem' }}>
                  {selectedCategory === 'vegetable' ? 'Step 3' : 'Step 2'}: Choose Plant ({filteredPlants.length} available)
                </label>
                <select 
                  value={selectedPlant}
                  onChange={(e) => {
                    setSelectedPlant(e.target.value)
                    setShowPlantInfo(e.target.value || null)
                  }}
                  style={{ width: '100%', padding: '0.5rem' }}
                >
                  <option value="">-- Select Plant --</option>
                  {filteredPlants.map(plant => (
                    <option key={plant.id} value={plant.id}>
                      {plant.name} ({plant.daysToHarvest} days)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Add Button */}
            {selectedPlant && (
              <button 
                className="btn btn-primary" 
                onClick={addPlantToCalendar}
                style={{ marginTop: '0.75rem', width: '100%' }}
              >
                Add to Calendar
              </button>
            )}

            {/* Selected Plants List */}
            {selectedPlants.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Your Plants ({selectedPlants.length})</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {selectedPlants.map(plantId => {
                    const plant = plants.find(p => p.id === plantId)
                    return (
                      <div 
                        key={plantId}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.5rem 0.75rem',
                          background: '#f5f5f5',
                          borderRadius: '4px',
                          fontSize: '0.9rem'
                        }}
                      >
                        <span 
                          style={{ cursor: 'pointer', flex: 1 }}
                          onClick={() => setShowPlantInfo(plantId)}
                        >
                          {plant?.name}
                        </span>
                        <button 
                          onClick={() => removePlantFromCalendar(plantId)}
                          style={{ background: 'none', border: 'none', color: '#d9534f', cursor: 'pointer', fontSize: '1.2rem', padding: '0 0.25rem' }}
                        >
                          ×
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Middle Column - Plant Info */}
        <div>
          {plantInfoData ? (
            <div className="card">
              <h3>📖 Growing Guide</h3>
              <h4 style={{ marginTop: '0.5rem', color: '#2d5a27' }}>{plantInfoData.name}</h4>
              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>{plantInfoData.description}</p>
              
              {/* Quick Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '1rem' }}>
                <div style={{ padding: '0.5rem', background: '#f0f8ff', borderRadius: '4px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>Difficulty</div>
                  <div style={{ fontWeight: 600, color: getDifficulty(plantInfoData) === 'Easy' ? '#5cb85c' : getDifficulty(plantInfoData) === 'Advanced' ? '#d9534f' : '#f0ad4e' }}>
                    {getDifficulty(plantInfoData)}
                  </div>
                </div>
                <div style={{ padding: '0.5rem', background: '#f0f8ff', borderRadius: '4px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>Harvest</div>
                  <div style={{ fontWeight: 600 }}>{plantInfoData.daysToHarvest} days</div>
                </div>
              </div>

              {/* Growing Requirements */}
              <div style={{ marginTop: '1rem' }}>
                <h5 style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>🌱 Growing Requirements</h5>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#f9f9f9', borderRadius: '4px' }}>
                    <span>☀️ Sun:</span>
                    <span style={{ textTransform: 'capitalize' }}>{plantInfoData.sun} ({plantInfoData.sun === 'full' ? '6+ hrs' : plantInfoData.sun === 'partial' ? '3-6 hrs' : '<3 hrs'})</span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#f9f9f9', borderRadius: '4px' }}>
                    <span>💧 Water:</span>
                    <span style={{ textTransform: 'capitalize' }}>{plantInfoData.water}</span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#f9f9f9', borderRadius: '4px' }}>
                    <span>🌡️ Hardiness:</span>
                    <span>Zone {plantInfoData.hardinessZones}</span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#f9f9f9', borderRadius: '4px' }}>
                    <span>📏 Spacing:</span>
                    <span>{plantInfoData.spacing}" × {plantInfoData.rowSpacing}"</span>
                  </div>
                </div>
              </div>

              {/* Soil Information */}
              <div style={{ marginTop: '1rem' }}>
                <h5 style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>🌍 Soil Requirements</h5>
                
                <div style={{ padding: '0.75rem', background: '#fff8e1', borderRadius: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span>Type:</span>
                    <span style={{ textTransform: 'capitalize' }}>{plantInfoData.soil}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span>pH Level:</span>
                    <span>{plantInfoData.phMin ?? 6.0} - {plantInfoData.phMax ?? 7.0} ({getPHDescription(plantInfoData.phMin ?? 6.0, plantInfoData.phMax ?? 7.0)})</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                    💡 <strong>Soil Tip:</strong> {plantInfoData.soil === 'loamy' 
                      ? 'Loamy soil is ideal - rich, well-draining, and easy to work with.' 
                      : plantInfoData.soil === 'sandy' 
                        ? 'Sandy soil drains quickly. Add compost to improve water retention.'
                        : plantInfoData.soil === 'clay'
                          ? 'Clay soil holds moisture but can be heavy. Add organic matter to improve drainage.'
                          : 'Most soil types work. Focus on adding compost for nutrients.'}
                  </div>
                </div>
              </div>

              {/* Beginner Tips */}
              <div style={{ marginTop: '1rem' }}>
                <h5 style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>💡 Beginner Tips</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {getBeginnerTips(plantInfoData).map((tip, idx) => (
                    <div key={idx} style={{ padding: '0.5rem', background: '#e8f5e9', borderRadius: '4px', fontSize: '0.85rem' }}>
                      {tip}
                    </div>
                  ))}
                </div>
              </div>

              {/* Companions & Enemies */}
              {(plantInfoData.companions.length > 0 || plantInfoData.enemies.length > 0) && (
                <div style={{ marginTop: '1rem' }}>
                  {plantInfoData.companions.length > 0 && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <h5 style={{ fontSize: '0.9rem', marginBottom: '0.25rem', color: '#5cb85c' }}>✓ Good Companions</h5>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {plantInfoData.companions.map(companion => (
                          <span key={companion} style={{ padding: '0.25rem 0.5rem', background: '#e8f5e9', borderRadius: '4px', fontSize: '0.8rem' }}>
                            {companion}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {plantInfoData.enemies.length > 0 && (
                    <div>
                      <h5 style={{ fontSize: '0.9rem', marginBottom: '0.25rem', color: '#d9534f' }}>✗ Avoid Planting Near</h5>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {plantInfoData.enemies.map(enemy => (
                          <span key={enemy} style={{ padding: '0.25rem 0.5rem', background: '#ffebee', borderRadius: '4px', fontSize: '0.8rem' }}>
                            {enemy}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌱</div>
              <p>Select a plant to view detailed growing information</p>
            </div>
          )}
        </div>

        {/* Right Column - Calendar & Tasks */}
        <div>
          {/* Month Selector */}
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ marginBottom: '0.75rem' }}>📆 {fullMonthNames[selectedMonth]} {selectedYear}</h3>
            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
              {monthNames.map((month, index) => (
                <button
                  key={month}
                  className="btn"
                  style={{
                    background: selectedMonth === index ? '#2d5a27' : '#f5f5f5',
                    color: selectedMonth === index ? 'white' : '#333',
                    padding: '0.4rem 0.6rem',
                    fontSize: '0.85rem',
                    flex: '1',
                    minWidth: '40px'
                  }}
                  onClick={() => setSelectedMonth(index)}
                >
                  {month}
                </button>
              ))}
            </div>
          </div>

          {/* Tasks for Selected Month */}
          <div className="card">
            <h3 style={{ marginBottom: '0.75rem' }}>📝 Tasks ({tasksByMonth.length})</h3>
            
            {!zone ? (
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Enter your zip code to see your personalized planting calendar.
              </p>
            ) : selectedPlants.length === 0 ? (
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Select plants to add them to your calendar.
              </p>
            ) : tasksByMonth.length === 0 ? (
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                No tasks for {fullMonthNames[selectedMonth]}.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '500px', overflow: 'auto' }}>
                {tasksByMonth.map(task => (
                  <div
                    key={task.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      background: task.completed ? '#e8f5e9' : 'white',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      opacity: task.completed ? 0.6 : 1
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={task.completed}
                      onChange={() => toggleTask(task.id)}
                      style={{ width: '18px', height: '18px', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {task.plantName}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: getTaskColor(task.task), fontWeight: 500 }}>
                        {getTaskLabel(task.task)}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666', textAlign: 'right', flexShrink: 0 }}>
                      {task.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Year Overview */}
          {tasks.length > 0 && (
            <div className="card" style={{ marginTop: '1rem' }}>
              <h3 style={{ marginBottom: '0.75rem', fontSize: '1rem' }}>Year Overview</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.5rem' }}>
                {monthNames.map((month, index) => {
                  const monthTasks = tasks.filter(t => t.date.getMonth() === index)
                  const hasTasks = monthTasks.length > 0
                  return (
                    <div 
                      key={month}
                      onClick={() => setSelectedMonth(index)}
                      style={{
                        padding: '0.5rem',
                        background: selectedMonth === index ? '#2d5a27' : hasTasks ? '#e8f5e9' : '#f5f5f5',
                        color: selectedMonth === index ? 'white' : hasTasks ? '#2d5a27' : '#666',
                        borderRadius: '4px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{month}</div>
                      <div style={{ fontSize: '0.75rem' }}>{monthTasks.length} tasks</div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Calendar
